import { Component, EventEmitter, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { generateTableColumns } from '@ui-kit/webkit/components/crud-item/crud-item-decorator';
import { AppTableDisplayColumn, TableActionItem } from '@ui-kit/webkit/components/crud-table/crud-table.model';
import { EditEquipmentDialogComponent } from '../atoku-storage-equipment/dialogs/edit-equipment-dialog/edit-equipment-dialog.component';
import { CrudAtokuInventoryItem } from '../models/crud-atoku-inventory-item.model';
import { AtokuEquipment } from '../models/atoku-equipment.model';
import { AtokuPricingProfileLookup, AtokuService } from '../services/atoku.service';

@Component({
    selector: 'app-atoku-storage',
    templateUrl: './atoku-storage.component.html',
    styleUrls: ['./atoku-storage.component.scss']
})
export class AtokuStorageComponent implements OnInit {
    readonly crudInventoryModel = CrudAtokuInventoryItem;

    displayColumns: AppTableDisplayColumn[] = generateTableColumns(CrudAtokuInventoryItem);
    pricingProfiles: AtokuPricingProfileLookup[] = [];
    selectedPricingProfileCode = '';
    tableData: CrudAtokuInventoryItem[] = [];

    private items: AtokuEquipment[] = [];
    private selectedProfilePrices: Record<string, number> = {};

    private readonly refreshItemsAction = new EventEmitter<void>();
    private readonly createItemAction = new EventEmitter<void>();
    private readonly editItemAction = new EventEmitter<CrudAtokuInventoryItem>();
    private readonly removeItemAction = new EventEmitter<CrudAtokuInventoryItem>();

    readonly additionalActions: TableActionItem[] = [
        {
            icon: 'refresh',
            label: 'Refresh data',
            actionDataSource: 'None',
            enabled: 'Always',
            includeData: false,
            eventEmitter: this.refreshItemsAction,
            keepSelectedRowOnClick: false
        },
        {
            icon: 'add',
            label: 'Create storage',
            actionDataSource: 'None',
            enabled: 'Always',
            includeData: false,
            eventEmitter: this.createItemAction,
            keepSelectedRowOnClick: false
        },
        {
            icon: 'edit',
            label: 'Edit storage',
            actionDataSource: 'SelectedRow',
            enabled: 'RowSelected',
            includeData: true,
            eventEmitter: this.editItemAction,
            keepSelectedRowOnClick: false
        },
        {
            icon: 'delete',
            label: 'Remove storage',
            actionDataSource: 'SelectedRow',
            enabled: 'RowSelected',
            includeData: true,
            eventEmitter: this.removeItemAction,
            keepSelectedRowOnClick: false
        }
    ];

    constructor(
        private readonly dialog: MatDialog,
        private readonly atokuService: AtokuService
    ) {
        this.refreshItemsAction.subscribe(() => this.refreshItems());
        this.createItemAction.subscribe(() => this.openAddDialog());
        this.editItemAction.subscribe((item) => this.openEditDialog(item));
        this.removeItemAction.subscribe((item) => this.removeItem(item));
        this.updatePriceColumnLabel();
        this.updateNameColumnLabel('Storage Unit');
    }

    ngOnInit(): void {
        this.loadProfiles();
        this.loadItems();
    }

    onPricingProfileChange(): void {
        this.updatePriceColumnLabel();

        if (!this.selectedPricingProfileCode) {
            this.selectedProfilePrices = {};
            this.rebuildTableData();
            return;
        }

        this.atokuService.getPricingProfiles().subscribe(profiles => {
            const selectedProfile = profiles.find(profile => profile.code === this.selectedPricingProfileCode);
            this.selectedProfilePrices = this.toPriceMap(selectedProfile?.['storage']);
            this.rebuildTableData();
        });
    }

    private openAddDialog(): void {
        const dialogRef = this.dialog.open(EditEquipmentDialogComponent, {
            width: '500px',
            data: {
                id: this.getNextId(),
                equipmentName: '',
                totalAmount: 0,
                bookedLast30Days: 0,
                type: 'storage'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }

            this.atokuService.createEquipment({
                name: result.equipmentName,
                equipmentName: result.equipmentName,
                totalAmount: Number(result.totalAmount),
                bookedLast30Days: Number(result.bookedLast30Days),
                type: 'storage'
            }).subscribe(() => this.loadItems());
        });
    }

    private openEditDialog(item: CrudAtokuInventoryItem): void {
        const dialogRef = this.dialog.open(EditEquipmentDialogComponent, {
            width: '500px',
            data: {
                id: item.id,
                equipmentName: item.name,
                totalAmount: item.totalAmount,
                bookedLast30Days: item.bookedLast30Days,
                price: item.price,
                type: 'storage'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                return;
            }

            this.atokuService.updateEquipment(result.id, {
                name: result.equipmentName,
                equipmentName: result.equipmentName,
                totalAmount: Number(result.totalAmount),
                bookedLast30Days: Number(result.bookedLast30Days),
                type: 'storage'
            }).subscribe(() => this.loadItems());
        });
    }

    private removeItem(item: CrudAtokuInventoryItem): void {
        this.atokuService.deleteEquipment(item.id, 'storage').subscribe(() => this.loadItems());
    }

    private refreshItems(): void {
        this.loadProfiles();
        this.loadItems();
    }

    private loadItems(): void {
        this.atokuService.getEquipment('storage').subscribe(items => {
            this.items = items.map(item => {
                const raw = item as unknown as Record<string, unknown>;
                return {
                    id: String(raw['id'] ?? ''),
                    equipmentName: String(raw['equipmentName'] ?? raw['name'] ?? ''),
                    totalAmount: Number(raw['totalAmount'] ?? 0),
                    bookedLast30Days: Number(raw['bookedLast30Days'] ?? 0),
                    price: Number(raw['price'] ?? 0)
                };
            });
            this.rebuildTableData();
        });
    }

    private loadProfiles(): void {
        this.atokuService.getPricingProfileLookup().subscribe(lookup => {
            this.pricingProfiles = lookup;
            if (!lookup.some(profile => profile.code === this.selectedPricingProfileCode)) {
                this.selectedPricingProfileCode = lookup[0]?.code ?? '';
            }
            this.onPricingProfileChange();
        });
    }

    private rebuildTableData(): void {
        this.tableData = this.items
            .map(item => new CrudAtokuInventoryItem({
                id: String(item.id ?? ''),
                name: item.equipmentName ?? '',
                totalAmount: Number(item.totalAmount ?? 0),
                bookedLast30Days: Number(item.bookedLast30Days ?? 0),
                price: this.getSelectedProfilePrice(item)
            }));
    }

    private getSelectedProfilePrice(item: AtokuEquipment): number {
        const mapped = this.selectedProfilePrices[String(item.id)];
        if (mapped !== undefined) {
            return mapped;
        }
        return Number(item.price ?? 0);
    }

    private updateNameColumnLabel(label: string): void {
        const nameColumn = this.displayColumns.find(column => column.colName === 'name');
        if (nameColumn) {
            nameColumn.displayLabel = label;
            nameColumn.exportLabel = label.toUpperCase();
            this.displayColumns = [...this.displayColumns];
        }
    }

    private updatePriceColumnLabel(): void {
        const priceColumn = this.displayColumns.find(column => column.colName === 'price');
        if (priceColumn) {
            priceColumn.displayLabel = this.selectedPricingProfileCode
                ? `Price (${this.selectedPricingProfileCode})`
                : 'Price';
            priceColumn.exportLabel = priceColumn.displayLabel.toUpperCase();
            this.displayColumns = [...this.displayColumns];
        }
    }

    private getNextId(): number {
        const numericIds = this.items
            .map(item => Number(item.id))
            .filter(id => Number.isFinite(id));
        return numericIds.length ? Math.max(...numericIds) + 1 : 1;
    }

    private toPriceMap(prices: unknown): Record<string, number> {
        if (!prices) {
            return {};
        }
        if (Array.isArray(prices)) {
            return prices.reduce<Record<string, number>>((acc, item) => {
                const row = item as Record<string, unknown>;
                const id = String(row['id'] ?? row['Id'] ?? '');
                if (!id) {
                    return acc;
                }
                acc[id] = Number(row['price'] ?? row['Price'] ?? 0);
                return acc;
            }, {});
        }
        if (typeof prices === 'object') {
            return Object.entries(prices as Record<string, unknown>).reduce<Record<string, number>>((acc, [key, value]) => {
                acc[String(key)] = Number(value ?? 0);
                return acc;
            }, {});
        }
        return {};
    }
}
